import { GridFilterModel, GridLogicOperator } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { default as utc } from 'dayjs/plugin/utc';

dayjs.extend(utc);

// TODO add support for the quick filter. Maybe a map of fields to apply quick filter search to?
export const getRsqlString = (model: GridFilterModel, fieldNameMap: Record<string, string> = {}): string => {
  let filterString = '';
  // Choose delimiter based on logical operator. Default to AND
  // ; = AND
  // , = OR
  let delimiter: string;
  switch (model.logicOperator) {
    case GridLogicOperator.Or:
      delimiter = ',';
      break;
    case GridLogicOperator.And:
    default:
      delimiter = ';';
  }

  // Iterate over items
  model.items
    .filter(item => item.value !== undefined && item.value !== '')
    .forEach((item, index) => {
      const fieldName = fieldNameMap[item.field] ?? item.field;
      let value = item.value;
      let operator: string;

      // Logic for different filter operators
      switch (item.operator) {
        case 'contains':
          // Surround string value with * wildcard
          value = `*${value}*`;
          operator = '==';
          break;
        case 'startsWith':
          value = `${value}*`;
          operator = '==';
          break;
        case 'endsWith':
          value = `*${value}`;
          operator = '==';
          break;
        case 'is':
          // Date comparison
          value = formatDate(value);
          operator = '==';
          break;
        case 'not':
          // Date comparison
          value = formatDate(value);
          operator = '!=';
          break;
        case 'after':
          // Date comparison
          value = formatDate(value);
          operator = '>';
          break;
        case 'onOrAfter':
          // Date comparison
          value = formatDate(value);
          operator = '>=';
          break;
        case 'before':
          // Date comparison
          value = formatDate(value);
          operator = '<';
          break;
        case 'onOrBefore':
          // Date comparison
          value = formatDate(value);
          operator = '<=';
          break;
        case '=':
          operator = '==';
          break;
        case '!=':
        case '>':
        case '>=':
        case '<':
        case '<=':
          operator = item.operator;
          break;
        case 'equals':
        default:
          // Default to just using equal comparison
          operator = '==';
      }

      // Append field + operator + value to filterString
      // If index !== 0, add delimiter
      if (index !== 0) {
        filterString += delimiter;
      }
      filterString += `${fieldName}${operator}${value}`;
    });

  return filterString;
};

const formatDate = (value: string) => {
  const parsedDate = dayjs.utc(value);
  if (parsedDate.isValid()) {
    return parsedDate.toISOString();
  }
  return value;
};

export enum LogicalOperator {
  OR = 'or',
  AND = 'and'
}

export enum FilterOperator {
  CONTAINS = 'contains',
  STARTS_WITH = 'startsWith',
  ENDS_WITH = 'endsWith',
  IS = 'is',
  NOT = 'not',
  AFTER = 'after',
  ON_OR_AFTER = 'onOrAfter',
  BEFORE = 'before',
  ON_OR_BEFORE = 'onOrBefore',
  EQUALS = '==',
  NOT_EQUALS = '!=',
  GREATER_THAN = '>',
  GREATER_THAN_OR_EQUALS = '>=',
  LESS_THAN = '<',
  LESS_THAN_OR_EQUALS = '<='
}

export type FilterState = {
  logicalOperator: LogicalOperator;
  filters: Filter[];
};

export type Filter = {
  field: string;
  value: any;
  filterOperator: FilterOperator;
};

export const filtersToRsqlString = (filter: FilterState, fieldNameMap: Record<string, string> = {}) => {
  let filterString = '';
  // Choose delimiter based on logical operator. Default to AND
  // ; = AND
  // , = OR
  let delimiter: string;
  switch (filter.logicalOperator) {
    case LogicalOperator.OR:
      delimiter = ',';
      break;
    case LogicalOperator.AND:
    default:
      delimiter = ';';
  }

  // Iterate over items
  filter.filters
    .filter(item => item.value !== undefined && item.value !== '')
    .forEach((item, index) => {
      const fieldName = fieldNameMap[item.field] ?? item.field;
      let value = item.value;
      let operator: string;

      // Logic for different filter operators
      switch (item.filterOperator) {
        case 'contains':
          // Surround string value with * wildcard
          value = `*${value}*`;
          operator = '==';
          break;
        case 'startsWith':
          value = `${value}*`;
          operator = '==';
          break;
        case 'endsWith':
          value = `*${value}`;
          operator = '==';
          break;
        case 'is':
          // Date comparison
          value = formatDate(value);
          operator = '==';
          break;
        case 'not':
          // Date comparison
          value = formatDate(value);
          operator = '!=';
          break;
        case 'after':
          // Date comparison
          value = formatDate(value);
          operator = '>';
          break;
        case 'onOrAfter':
          // Date comparison
          value = formatDate(value);
          operator = '>=';
          break;
        case 'before':
          // Date comparison
          value = formatDate(value);
          operator = '<';
          break;
        case 'onOrBefore':
          // Date comparison
          value = formatDate(value);
          operator = '<=';
          break;
        case '==':
        case '!=':
        case '>':
        case '>=':
        case '<':
        case '<=':
          operator = item.filterOperator;
          break;
        default:
          // Default to just using equal comparison
          operator = '==';
      }

      // Append field + operator + value to filterString
      // If index !== 0, add delimiter
      if (index !== 0) {
        filterString += delimiter;
      }
      filterString += `${fieldName}${operator}"${value}"`;
    });

  if (!!filterString) {
    filterString = `(${filterString})`;
  }
  return filterString;
};

/**
 * Combines RSQL fragments with AND (`;`) or OR (`,`) into one filter string.
 *
 * Empty fragments (null, undefined, false, whitespace-only) are dropped, so the result never starts,
 * ends or repeats an operator; a lone fragment is returned unchanged. Two or more fragments are each
 * wrapped in parentheses, because `;` binds tighter than `,` in RSQL: without grouping,
 * `a==1,b==2` AND `c==3` would read as `a==1,(b==2;c==3)`.
 *
 * AND joins must stay `;`-separated: several backend CSV exports split the filter on `;` to find
 * the `location.id` clause.
 */
export const joinRsql = (operator: LogicalOperator, ...fragments: Array<string | null | undefined | false>): string => {
  const present = fragments.filter((fragment): fragment is string => !!fragment && fragment.trim() !== '');
  if (present.length <= 1) {
    return present[0] ?? '';
  }
  const delimiter = operator === LogicalOperator.OR ? ',' : ';';
  return present.map(fragment => `(${fragment})`).join(delimiter);
};
